import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { EPermissions } from '@auth/login/data/login.model';
import {
  GoogleAnalyticsAction,
  GoogleAnalyticsCategory,
} from '@shared/google-analytics/google-analytics.model';
import { GoogleAnalyticsService } from '@shared/google-analytics/google-analytics.service';
import { PermissionService } from '@shared/services/util/permission.service';
import { isEmpty } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Link, MenuPoints, Section } from '@shared/components/header/menu-points.model';
import { IconColor, IconWeight } from '@widgets/eop-icon/eop-icon.component';
import { environment } from 'environments/environment';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';

@Component({
  selector: 'eop-menu-group',
  templateUrl: './menu-group.component.html',
  styleUrls: ['./menu-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MenuGroupComponent implements OnInit {
  private readonly permissionService = inject(PermissionService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() menuPoints: MenuPoints;
  @Input() menuCollapsed: boolean;
  @Input() activeGroupName: string;

  @Output() activeGroupChange: EventEmitter<string> = new EventEmitter<string>();

  readonly IconWeight = IconWeight;
  readonly IconColor = IconColor;
  readonly environment = environment;
  /** Int-only debug overlay: shows the GrowthBook flag id on every menu item (toggled live). */
  ffDebug = this.featureFlagService.debugEnabled === true;
  panelIndex = 0;

  userHasInternalRole: boolean;

  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.userHasInternalRole = this.permissionService.userHasInternalRole();
    // Re-render menu when GrowthBook features refresh (SSE / re-init).
    this.featureFlagService.changes$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.cdr.markForCheck());
    // Int-only debug overlay toggle (floating GrowthBook panel).
    this.featureFlagService.debugEnabled$?.pipe(takeUntil(this.unsubscribe$)).subscribe(enabled => {
      this.ffDebug = enabled === true;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Link visibility. All conditions must pass:
   *   1. Internal-role restriction (`checkIfInternalRole`).
   *   2. Permissions (`link.hidden`).
   *   3. GrowthBook flag `link.growthbookFlagId` is `true`
   *      (fail-closed — a flag missing in GrowthBook hides the link).
   */
  isVisible(link: Link): boolean {
    if (link.checkIfInternalRole && !this.userHasInternalRole) {
      return false;
    }
    if (!this.checkPermission(link.hidden)) {
      return false;
    }
    return this.featureFlagService.isMenuItemVisible(link.growthbookFlagId);
  }

  /**
   * Section (parent menu item) visibility. All conditions must pass:
   *   1. Permissions (`section.hidden`).
   *   2. GrowthBook flag `section.growthbookFlagId` is `true`
   *      (fail-closed — a missing flag hides the section with all its links).
   *   3. At least one of its links is visible.
   */
  isSectionVisible(section: Section): boolean {
    return (
      this.checkPermission(section.hidden) &&
      this.featureFlagService.isMenuItemVisible(section.growthbookFlagId) &&
      (section.links ?? []).some(l => this.isVisible(l))
    );
  }

  checkPermission(permissions: EPermissions[]): boolean {
    if (isEmpty(permissions)) return true;
    const permissionsArray = [];
    permissions.forEach(permission => {
      permissionsArray.push(this.hasPermission(permission));
    });
    return permissionsArray.includes(true);
  }

  private hasPermission(permission: EPermissions): boolean {
    return this.permissionService.userHasPermission(permission);
  }

  sendAnalyticEvent(route: string) {
    this.googleAnalyticsService.sendEvent(
      GoogleAnalyticsCategory.SIDE_MENU,
      GoogleAnalyticsAction.NAVIGATE,
      route
    );
  }
  onActivateLink(panelIndexToExpand: number) {
    this.setOpenedPanel(panelIndexToExpand);
  }

  setOpenedPanel(index: number): void {
    this.panelIndex = index;
    const groupName = this.menuPoints.sections[index].name;
    if (this.activeGroupName !== groupName) {
      this.activeGroupChange.emit(this.menuPoints.sections[index].name);
    }
  }

  isExpanded(index: number): boolean {
    return (
      this.panelIndex === index && this.menuPoints.sections[index].name === this.activeGroupName
    );
  }
}
